import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  getSubstitutionEligibility,
  type GetSubstitutionEligibilityParams,
  type SubstitutionEligibility,
  type SubstitutionEligibilityResponse,
} from '@/lib/network';

export const SUBSTITUTION_ELIGIBILITY_QUERY_KEY_PREFIX = ['substitutions', 'eligibility'] as const;
const SUBSTITUTION_ELIGIBILITY_STALE_TIME_MS = 10 * 1000;
const SUBSTITUTION_ELIGIBILITY_GC_TIME_MS = 5 * 60 * 1000;

export const getSubstitutionEligibilityQueryKey = (
  teamId: string | undefined,
  matchdayId: string | undefined,
  gameweekId: string | undefined
) =>
  [
    ...SUBSTITUTION_ELIGIBILITY_QUERY_KEY_PREFIX,
    teamId ?? '',
    matchdayId ?? '',
    gameweekId ?? '',
  ] as const;

export type UseSubstitutionEligibilityOptions = Omit<
  UseQueryOptions<SubstitutionEligibilityResponse, Error, SubstitutionEligibility>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useSubstitutionEligibility = (
  params: Partial<GetSubstitutionEligibilityParams>,
  options?: UseSubstitutionEligibilityOptions
): UseQueryResult<SubstitutionEligibility, Error> => {
  const { enabled = true, ...restOptions } = options ?? {};
  const teamId = params.teamId;
  const matchdayId = params.matchdayId;
  const gameweekId = params.gameweekId;

  return useQuery<SubstitutionEligibilityResponse, Error, SubstitutionEligibility>({
    queryKey: getSubstitutionEligibilityQueryKey(teamId, matchdayId, gameweekId),
    queryFn: async () => {
      if (!teamId || (!matchdayId && !gameweekId)) {
        throw new Error(
          'teamId and either matchdayId or gameweekId are required to fetch substitution eligibility'
        );
      }
      return getSubstitutionEligibility({ teamId, matchdayId, gameweekId });
    },
    select: response => response.data,
    enabled: enabled && !!teamId && (!!matchdayId || !!gameweekId),
    staleTime: SUBSTITUTION_ELIGIBILITY_STALE_TIME_MS,
    gcTime: SUBSTITUTION_ELIGIBILITY_GC_TIME_MS,
    ...restOptions,
  });
};
